import {
  DEFAULT_RISK_PROFILE,
  INDIA_MARKET_MODEL,
  INFLATION_MEAN,
  RISK_PROFILES,
} from './assumptions/india'
import type { MarketModel, SimInputs } from './engine/types'
import { taxAdjustedFireMultiple, withdrawalGrossUp } from './tax/withdrawal'
import type { WithdrawalTaxInputs } from './tax/withdrawal'

/**
 * The user's plan: the six things they control, plus everything needed to turn
 * those into engine inputs.
 *
 * Bounds live here rather than on the sliders because two very different callers
 * need to agree on them. The sliders use them to lay out their travel; the URL
 * codec uses them to clamp. A URL is untrusted input — see the note on
 * `clampPlan` for what happens without that.
 */

export interface Plan {
  currentAge: number
  fireByAge: number
  currentCorpus: number
  monthlyInvestment: number
  annualExpensesToday: number
  /** Matches an id in RISK_PROFILES. */
  profileId: string
  /**
   * Long-run inflation, as a decimal. The only *belief* among these fields —
   * everything else is a fact about the user.
   *
   * It is a control rather than a constant because it is both the highest-
   * leverage number in the model and the most contested: over 40 years, 4%
   * compounds to 4.80x and 6% to 10.29x, a 2.14x difference in the FIRE number.
   * Picking one on the user's behalf means either being called optimistic or
   * being called pessimistic, and both are correct depending on who is asking.
   * Handing it over answers the objection instead of arguing with it.
   */
  inflationMean: number
}

export interface Bound {
  min: number
  max: number
  /**
   * Money spans four orders of magnitude, so a linear slider puts every
   * realistic value in the first few percent of travel. Log gives the whole
   * range usable resolution under a thumb.
   */
  scale: 'linear' | 'log'
}

export const BOUNDS = {
  currentAge: { min: 18, max: 60, scale: 'linear' },
  fireByAge: { min: 25, max: 70, scale: 'linear' },
  currentCorpus: { min: 0, max: 100_000_000, scale: 'log' },
  monthlyInvestment: { min: 0, max: 1_000_000, scale: 'log' },
  annualExpensesToday: { min: 120_000, max: 12_000_000, scale: 'log' },
  /**
   * 2% to 10%. The lower bound is the floor of the RBI's tolerance band; the
   * upper is well past its ceiling, deliberately, so someone who thinks the
   * band is fiction can model that and see what it costs them.
   */
  inflationMean: { min: 0.02, max: 0.1, scale: 'linear' },
} as const satisfies Record<string, Bound>

/**
 * The person who actually opens a Monte Carlo FIRE tool.
 *
 * The chart is alive on arrival — there is no empty state anywhere in this
 * product, because the entire funnel is link → move a slider → see your number,
 * and an empty chart breaks it at step one.
 *
 * ── Why these values ────────────────────────────────────────────────────────
 * A 32-year-old with ₹50L invested, putting away ₹1L a month, spending ₹1L a
 * month, aiming at 50. That is the r/FIREindia median far more than the previous
 * default was: someone who seeks out a probabilistic FIRE simulator has already
 * been saving hard for a decade, and the old ₹10L-corpus / ₹50k-a-month figures
 * described someone who has not started.
 *
 * ── Why not a more flattering one ───────────────────────────────────────────
 * The old default landed on 3%, which reads as "you are doomed" before the user
 * has touched anything. The fix is NOT to tune the inputs until the headline is
 * 90%: that is demo-rigging, it is exactly what r/FIREindia dismantles first, and
 * it would undercut the substantiation position the whole disclaimer rests on.
 *
 * These values land near 60%, and that is the right place for a different reason.
 * A default that already reads 90% is *less* engaging, not more — nothing the
 * user drags visibly changes it, so there is no reason to touch the sliders. Near
 * 60% every control has visible consequence, which is the entire product. It also
 * leaves the 75% celebration in useThresholdCelebration something to be *earned*
 * by dragging; that moment deliberately never fires on arrival, so a default
 * above the threshold would mean nobody ever sees it.
 *
 * The number moves with INFLATION_MEAN, which is a separate and deliberately
 * independent decision — these values were chosen for being representative, not
 * for what they produce against any particular inflation assumption.
 */
export const DEFAULT_PLAN: Plan = {
  currentAge: 32,
  fireByAge: 50,
  currentCorpus: 5_000_000,
  monthlyInvestment: 100_000,
  annualExpensesToday: 1_200_000,
  profileId: DEFAULT_RISK_PROFILE.id,
  /**
   * From the dossier, not a literal. The value lives with its RBI anchor and its
   * reasoning in INFLATION_MEAN; duplicating it here is how the slider default
   * and the assumptions panel end up quietly disagreeing about what the product
   * assumes.
   */
  inflationMean: INFLATION_MEAN.value,
}

/**
 * How far past the target age the projection runs.
 *
 * Ten years, not "to age 70", and the reason is honesty rather than tidiness.
 * F1 models accumulation only — there are no withdrawals until F7. So every year
 * drawn after the target age depicts someone who has retired and then spends
 * nothing, compounding untouched. That is not a lucky outcome or an unlikely
 * one; it is not an outcome at all, and the longer it runs the more absurd it
 * gets: projecting a 45-year-old's 80%-equity portfolio to 70 with zero
 * withdrawals put the top of the fan at ₹120 Cr.
 *
 * That wrecked the chart it was drawn on. The y-axis has to reach the tallest
 * thing plotted, so the FIRE requirement line — the entire point of the picture —
 * rendered at 3.9% of the chart height, with the median at 2.5%. Ending ten years
 * past target puts them at 23% and 15%, with no clipping and nothing hidden.
 *
 * Ten specifically, because it is long enough to still show WHEN a missed target
 * is actually reached — the most useful thing on screen for someone who has not
 * made it — while dropping the years that only exist because a feature is
 * missing. When F7 adds the withdrawal phase this should be revisited: at that
 * point the post-retirement years become real and worth drawing.
 */
export const YEARS_PAST_TARGET = 10

/** Nobody is planning past this, and the arrays have to stop somewhere. */
export const MAX_PROJECTION_AGE = 85

/**
 * Not user-facing in F1. Exposing it would be a sixth control and would push the
 * layout off one screen; it belongs in F3's assumptions drawer. 5% is a modest
 * assumption for a salaried investor whose income rises.
 */
export const ANNUAL_STEP_UP = 0.05

function clamp(value: number, bound: Bound): number {
  if (!Number.isFinite(value)) return bound.min
  return Math.min(bound.max, Math.max(bound.min, value))
}

/**
 * Forces a plan into the modelled range.
 *
 * This is a load-bearing guard, not tidiness. Plans arrive from the URL hash,
 * which anyone can hand-edit. `currentAge` and `fireByAge` set the projection
 * horizon, and the engine allocates `paths x years` typed arrays from it — so an
 * unclamped `currentAge=-99999` would ask the worker for a multi-gigabyte
 * allocation and hang the tab. Clamping first means a malformed link renders a
 * sensible chart instead of a crash.
 */
export function clampPlan(plan: Plan): Plan {
  const currentAge = Math.round(clamp(plan.currentAge, BOUNDS.currentAge))
  const fireByAge = Math.round(clamp(plan.fireByAge, BOUNDS.fireByAge))

  return {
    currentAge,
    // Retiring before today is not a plan. The engine would clamp this itself,
    // but doing it here keeps the slider and the chart telling the same story.
    fireByAge: Math.max(currentAge + 1, fireByAge),
    currentCorpus: clamp(plan.currentCorpus, BOUNDS.currentCorpus),
    monthlyInvestment: clamp(plan.monthlyInvestment, BOUNDS.monthlyInvestment),
    annualExpensesToday: clamp(plan.annualExpensesToday, BOUNDS.annualExpensesToday),
    profileId: RISK_PROFILES.some((p) => p.id === plan.profileId)
      ? plan.profileId
      : DEFAULT_RISK_PROFILE.id,
    inflationMean: clamp(plan.inflationMean, BOUNDS.inflationMean),
  }
}

/** Resolves the plan's profile id to its allocation, falling back to the default. */
export function allocationFor(profileId: string) {
  return (RISK_PROFILES.find((p) => p.id === profileId) ?? DEFAULT_RISK_PROFILE).allocation
}

/**
 * The market model with the user's inflation belief substituted in.
 *
 * Only the long-run mean is user-editable. The volatility and persistence of the
 * inflation process stay as modelled: a user has an intuition about "how fast
 * prices rise", and none whatsoever about the unconditional standard deviation of
 * an AR(1) — exposing those would be handing over a dial nobody can turn
 * meaningfully.
 *
 * Memoise the result in the caller. `useSimulation` compares the model by
 * reference, so a fresh object every render would queue a fresh simulation.
 */
export function toMarketModel(plan: Plan): MarketModel {
  const safe = clampPlan(plan)
  return {
    ...INDIA_MARKET_MODEL,
    inflation: { ...INDIA_MARKET_MODEL.inflation, mean: safe.inflationMean },
  }
}

/**
 * The tax module's view of a plan.
 *
 * The withdrawal is inflated to the target age at the plan's own long-run mean,
 * deterministically. The engine inflates stochastically per path, but
 * `SimInputs.fireMultiple` is a single scalar, so the gross-up has to be solved
 * at one withdrawal rather than across a distribution.
 */
export function withdrawalTaxInputsFor(plan: Plan): WithdrawalTaxInputs {
  const safe = clampPlan(plan)
  const years = Math.max(0, safe.fireByAge - safe.currentAge)
  return {
    annualWithdrawalAtTarget: safe.annualExpensesToday * Math.pow(1 + safe.inflationMean, years),
    accumulationYears: years,
    contributionStepUpPct: ANNUAL_STEP_UP,
  }
}

/**
 * How much the withdrawal tax adds to the requirement, as a factor >= 1.
 *
 * The UI needs this to recover the PRE-tax figure from what the engine returned:
 * `result.requiredAtTarget / withdrawalGrossUpFor(plan)` is exactly the naive
 * number, because the gross-up is the only thing separating the two.
 */
export function withdrawalGrossUpFor(plan: Plan): number {
  return withdrawalGrossUp(withdrawalTaxInputsFor(plan))
}

/**
 * Converts a plan into what the engine consumes. Clamps on the way through.
 *
 * `projectToAge` is normally derived from this plan's own `fireByAge`. Pass an
 * override when several plans share one chart: two fans on one x-domain must run
 * to the same age, or the shorter one ends in a vertical cliff partway across.
 * `scenarios.ts` owns that decision — see `sharedHorizon`.
 */
export function toSimInputs(plan: Plan, projectToAge?: number): SimInputs {
  const safe = clampPlan(plan)
  return {
    currentAge: safe.currentAge,
    fireByAge: safe.fireByAge,
    // Always past the target, or the headline would be quoted against a point
    // beyond the end of the projection.
    projectToAge: Math.max(
      safe.fireByAge,
      projectToAge ?? Math.min(MAX_PROJECTION_AGE, safe.fireByAge + YEARS_PAST_TARGET),
    ),
    currentCorpus: safe.currentCorpus,
    monthlyInvestment: safe.monthlyInvestment,
    annualStepUpPct: ANNUAL_STEP_UP,
    annualExpensesToday: safe.annualExpensesToday,
    // From the dossier, never a literal — but no longer the bare dossier value.
    //
    // `FIRE_MULTIPLE` is the published withdrawal NORM and stays 30; what the
    // engine needs is that norm with the capital-gains tax a retiree pays on the
    // way out priced into it. So the F3 panel and the simulation still agree —
    // the panel quotes the rule, the engine quotes the rule plus the tax, and
    // the headline names both rupee figures so the difference is visible rather
    // than implied. `planCodec.test.ts` holds that relationship.
    fireMultiple: taxAdjustedFireMultiple(withdrawalTaxInputsFor(safe)),
    allocation: allocationFor(safe.profileId),
  }
}
